import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ServiceSection from '../components/services/ServiceSection';
import servicesEnhancedData from '../data/servicesEnhancedData';

import { SITE_URL } from '../components/GlobalSchema';
const ITEM_LIST_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'ABL Finance Financial Services',
  description:
    'Comprehensive financial services offered by ABL Finance including SBLC, Bank Guarantees, Trade Finance, Risk Management, Corporate Finance, and Asset Management.',
  url: `${SITE_URL}/services`,
  numberOfItems: 6,
  itemListElement: [
    'Trade Finance & SBLC Solutions',
    'Risk Management Services',
    'Corporate Finance Advisory',
    'Asset Management',
    'Tax & Regulatory Advisory',
    'Structured Finance Solutions'
  ].map((name, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Service',
      name,
      provider: { '@type': 'Organization', name: 'ABL Finance' },
      serviceType: name
    }
  }))
};

const BREADCRUMBS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` }
  ]
};

const TRUST_SIGNALS = [
  {
    title: '100+ Years Experience',
    body:
      'Our team brings over a century of combined expertise in financial services, delivering proven results across market cycles.'
  },
  {
    title: 'Global Network',
    body:
      'Strategic partnerships with leading financial institutions across multiple jurisdictions enable seamless cross-border solutions.'
  },
  {
    title: 'Client-Centric Approach',
    body:
      'Customized solutions tailored to your unique needs, backed by transparent communication and dedicated support.'
  }
];

const ServicesEnhanced = () => (
  <div className="min-h-screen">
    <Helmet>
      <title>Financial Services | SBLC, BG & Trade Finance | ABL Finance</title>
      <meta
        name="description"
        content="ABL Finance services: SBLC, Bank Guarantees, Trade Finance, Risk Management, Corporate Advisory, Asset Management, Tax and Structured Finance."
      />
      <meta
        name="keywords"
        content="ABL Finance, SBLC, standby letter of credit, bank guarantee, trade finance, risk management, corporate finance, M&A advisory, asset management, tax advisory, financial consulting"
      />
      <meta property="og:title" content="Financial Services | SBLC, BG & Trade Finance | ABL Finance" />
      <meta
        property="og:description"
        content="ABL Finance services: SBLC, Bank Guarantees, Trade Finance, Risk Management, Corporate Advisory, Asset Management and Structured Finance."
      />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="ABL Finance" />
      <meta property="og:url" content={`${SITE_URL}/services`} />
      <meta property="og:image" content={`${SITE_URL}/logo.png`} />
      <meta property="og:locale" content="en_GB" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Financial Services | SBLC, BG & Trade Finance | ABL Finance" />
      <meta name="twitter:description" content="ABL Finance services: SBLC, Bank Guarantees, Trade Finance, Risk Management, Corporate Advisory, Asset Management and Structured Finance." />
      <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
      <link rel="canonical" href={`${SITE_URL}/services`} />
      <script type="application/ld+json">{JSON.stringify(ITEM_LIST_SCHEMA)}</script>
      <script type="application/ld+json">{JSON.stringify(BREADCRUMBS_SCHEMA)}</script>
    </Helmet>

    {/* Hero */}
    <section className="relative py-20 bg-gradient-to-r from-slate-900 to-emerald-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6">Trade & Corporate Finance Services</h1>
        <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
          Comprehensive financial solutions backed by research, best practices, and over 65 years of combined expertise
        </p>
      </div>
    </section>

    {/* Sections */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-24">
          {servicesEnhancedData.map((service, index) => (
            <React.Fragment key={service.id}>
              <ServiceSection service={service} flipped={index % 2 === 1} />
              {index < servicesEnhancedData.length - 1 && (
                <hr className="border-slate-200" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>

    {/* Trust signals */}
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-slate-900 text-center mb-16">
          Why Choose Our Services
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {TRUST_SIGNALS.map((t) => (
            <Card key={t.title}>
              <CardHeader>
                <CardTitle className="text-xl">{t.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{t.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
        <p className="text-xl mb-8">
          Let our experts help you achieve your financial goals with tailored solutions and strategic guidance.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule a Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
          <a href="tel:+442032914022">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg"
            >
              Call Us Now
            </Button>
          </a>
        </div>
      </div>
    </section>

    {/* Last Updated */}
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <p className="text-sm text-slate-500 text-right">Last updated: May 2026</p>
    </div>
  </div>
);

export default ServicesEnhanced;
